import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { SEOHelmet } from 'components/SEO';
import { PlayPrimaryButton } from '../../components/PlayPrimaryButton';
import TextInputField from 'components/TextInputField/TextInputField';
import { useGetMeQuery } from 'store/services/user/meApi';
import { useJoinTournamentMutation, useLazyGetUsernameAvailabilityQuery } from 'store/services/play/playApi';
import { validationPatterns } from 'utils/inputSanitization';
import { playErrorCode, playErrorMessage } from 'utils/play/playErrors';
import { playPath } from 'utils/play/inviteUrl';
import type { PlayTournament, PlayViewer } from 'types/interfaces/play';
import { PlayLayout } from '../../layout/PlayLayout';
import './InvitePage.css';

interface Props {
  code: string;
  tournament: PlayTournament;
  prefill?: PlayViewer['prefill'];
}

type Availability = 'idle' | 'checking' | 'available' | 'taken' | 'invalid';

/**
 * Decision 6: one screen after auth. Username (prefilled, validated with the
 * frontend rule, inline Available state from GET /users/username-available),
 * First name and Last name (required). Submit joins with the three fields;
 * the server writes the username through the existing rule and 400
 * USERNAME_TAKEN is the backstop.
 */
const NameStepPage: React.FC<Props> = ({ code, tournament, prefill }) => {
  const navigate = useNavigate();
  const { data: me } = useGetMeQuery();
  const [join, { isLoading: joining }] = useJoinTournamentMutation();
  const [checkAvailability] = useLazyGetUsernameAvailabilityQuery();

  const [username, setUsername] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [availability, setAvailability] = useState<Availability>('idle');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const seeded = useRef(false);
  const checkTimer = useRef<number | null>(null);

  // Prefill once: the viewer's prefill from GET /tournaments/:code, else /users/me.
  useEffect(() => {
    if (seeded.current) return;
    const source = prefill ?? (me ? { username: me.username, firstName: me.firstName, lastName: me.lastName } : null);
    if (!source) return;
    seeded.current = true;
    setUsername(source.username ?? '');
    setFirstName(source.firstName ?? '');
    setLastName(source.lastName ?? '');
  }, [prefill, me]);

  const trimmed = username.trim();
  const isOwnName = !!me && trimmed.toLowerCase() === (me.username ?? '').toLowerCase();
  const matchesRule = validationPatterns.username.test(trimmed);

  useEffect(() => {
    if (checkTimer.current) window.clearTimeout(checkTimer.current);
    if (!trimmed) {
      setAvailability('idle');
      return;
    }
    if (!matchesRule) {
      setAvailability('invalid');
      return;
    }
    if (isOwnName) {
      setAvailability('available');
      return;
    }
    setAvailability('checking');
    checkTimer.current = window.setTimeout(() => {
      checkAvailability(trimmed)
        .unwrap()
        .then((res) => setAvailability(res.available ? 'available' : 'taken'))
        // A failed probe never blocks the player; the server's 400 USERNAME_TAKEN is the backstop.
        .catch(() => setAvailability('available'));
    }, 350);
    return () => {
      if (checkTimer.current) window.clearTimeout(checkTimer.current);
    };
  }, [trimmed, matchesRule, isOwnName, checkAvailability]);

  const canSubmit = useMemo(
    () => matchesRule && availability !== 'taken' && availability !== 'checking' && firstName.trim().length > 0 && lastName.trim().length > 0 && !joining,
    [matchesRule, availability, firstName, lastName, joining],
  );

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitError(null);
    try {
      const next = await join({ code, body: { username: trimmed, firstName: firstName.trim(), lastName: lastName.trim() } }).unwrap();
      navigate(next.tournament.decklistRequired && !next.viewer.dropped ? playPath(code, 'deck') : playPath(code), { replace: true });
    } catch (err) {
      const codeOf = playErrorCode(err);
      if (codeOf === 'USERNAME_TAKEN') setAvailability('taken');
      if (codeOf === 'USERNAME_INVALID') setAvailability('invalid');
      setSubmitError(playErrorMessage(err, "Couldn't join the tournament. Please try again."));
    }
  };

  const statusLine = (() => {
    switch (availability) {
      case 'checking':
        return <p className="play-field__status">Checking…</p>;
      case 'available':
        return <p className="play-field__status play-field__status--ok">Available</p>;
      case 'taken':
        return <p className="play-field__status play-field__status--bad">That username is taken</p>;
      case 'invalid':
        return <p className="play-field__status play-field__status--bad">3 to 20 letters, numbers, dashes or underscores</p>;
      default:
        return null;
    }
  })();

  return (
    <PlayLayout title={tournament.name} backTo={playPath(code)}>
      <SEOHelmet title={`Your player name - TradeMagic Play`} description="Set your player name for TradeMagic Play." noindex />
      <section className="play-card play-name-step" data-testid="play-name-step">
        <p className="play-eyebrow">One last step</p>
        <h1 className="play-title">Your player name</h1>
        <p className="play-helper">Shown on pairings and standings. Change it anytime from your profile.</p>

        <div className="play-field" style={{ marginTop: 18 }}>
          <label className="play-field__label" htmlFor="play-username">
            Username
          </label>
          <TextInputField value={username} placeholder="Username" onChange={setUsername} error={availability === 'taken' || availability === 'invalid'} />
          {statusLine}
        </div>
        <div className="play-field">
          <label className="play-field__label" htmlFor="play-first-name">
            First name
          </label>
          <TextInputField value={firstName} placeholder="First name" onChange={setFirstName} />
        </div>
        <div className="play-field">
          <label className="play-field__label" htmlFor="play-last-name">
            Last name
          </label>
          <TextInputField value={lastName} placeholder="Last name" onChange={setLastName} />
        </div>
        <p className="play-helper" style={{ marginTop: 10, fontSize: 12 }}>
          Your real name is visible only to the organizer. Other players see your username.
        </p>

        <div className="play-name-step__cta">
          <PlayPrimaryButton label="Join tournament" disabled={!canSubmit} loading={joining} onClick={submit} />
        </div>
        {submitError && (
          <p className="play-error" role="alert">
            {submitError}
          </p>
        )}
      </section>
    </PlayLayout>
  );
};

export default NameStepPage;
